import React, { useState, useEffect, useContext, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, MailOpen, Trash2, LogOut, Menu, X,
  MessageSquare, CheckCircle, Clock, User, Phone,
  ChevronRight, BarChart3, Inbox, Building2, FileText, Users, Shield, Eye, Server,
  ClipboardList, DollarSign, AlertCircle, Truck, TrendingDown, UserCheck, Receipt, Scale, Ship, TrendingUp, ShoppingBag, Package,
  ChevronLeft, Bell, BookOpen, PackageX, PackageCheck, LifeBuoy, Megaphone, BellRing, ShieldCheck, Repeat2, Settings, Fuel, ChefHat, CreditCard, HardDriveDownload, BedDouble, MapPin, Zap, SlidersHorizontal, HardHat, Stethoscope, Wrench
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthContext } from "../App";
import { toast } from "sonner";
import EmpresaSwitcher from "../components/EmpresaSwitcher";
import BackupModal from "../components/BackupModal";
import { LogoMarcaArandu, LogoMarcaJar, LogoMarcaAranduJar } from "../components/MarcaLogos";
import { resolveLogoForContext, logoSizePx } from "../lib/logoUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subCompact = "text-slate-400 text-[10px] tracking-wider font-body";

// Logo genérico Arandu&JAR (fallback)
const LogoAranduJAR = () => <LogoMarcaAranduJar compact sublabelClass={subCompact} />;

// Logo dinámico según empresa activa
const LogoDisplay = ({ activeEmpresaPropia }) => {
  // Resolver logo usando la configuración de contexto "panel"
  const logoUrl = resolveLogoForContext(activeEmpresaPropia, "panel");
  const sizePx  = logoSizePx(activeEmpresaPropia?.logo_panel_size || "m");

  if (logoUrl) {
    return (
      <div className="flex items-center" style={{ height: sizePx, maxWidth: 190 }}>
        <img
          src={logoUrl}
          alt={activeEmpresaPropia?.nombre || "Logo"}
          style={{ maxHeight: sizePx, maxWidth: 190, objectFit: "contain" }}
        />
      </div>
    );
  }
  // Sin logo resuelto: logo de texto de marca según slug
  const slug = activeEmpresaPropia?.slug;
  if (slug === "jar") {
    return <LogoMarcaJar compact sublabelClass={subCompact} />;
  }
  if (slug === "arandu") {
    return <LogoMarcaArandu compact sublabelClass={subCompact} />;
  }
  // arandujar o sin empresa seleccionada
  return <LogoAranduJAR />;
};

const mesActual = () => new Date().toISOString().slice(0, 7);
const prevMes = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, "0")}`;
};
const nextMes = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
};
const prevDia = (d) => {
  const dt = new Date(d + "T12:00:00");
  dt.setDate(dt.getDate() - 1);
  return dt.toISOString().slice(0, 10);
};
const nextDia = (d) => {
  const dt = new Date(d + "T12:00:00");
  dt.setDate(dt.getDate() + 1);
  return dt.toISOString().slice(0, 10);
};
const diaLabel = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T12:00:00");
  return dt.toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" });
};
const fmtPYG = (n) => `₲ ${Math.round(Number(n || 0)).toLocaleString("es-PY")}`;
const fmtUSD = (n) => `USD ${Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMixed = (pyg, usd, brl) => {
  const usdNum = Number(usd || 0);
  const brlNum = Number(brl || 0);
  return `${fmtPYG(pyg)}${usdNum ? ` / ${fmtUSD(usdNum)}` : ""}${brlNum ? ` / ${fmtBRL(brlNum)}` : ""}`;
};
const mesLabel = (m) => {
  if (!m) return "";
  const d = new Date(`${m}-02T00:00:00`);
  return d.toLocaleDateString("es-PY", { month: "short", year: "numeric" });
};


const AdminDashboard = () => {
  const { user, token, logout, hasPermission, hasModule, empresasPropias, activeEmpresaPropia } = useContext(AuthContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total_messages: 0, unread_messages: 0, read_messages: 0, total_empresas: 0, total_presupuestos: 0, presupuestos_borrador: 0, presupuestos_aprobados: 0, presupuestos_facturados: 0, presupuestos_cobrados: 0 });
  const [resumen, setResumen] = useState(null);
  const [cuotasStats, setCuotasStats] = useState(null);
  const [periodoTipo, setPeriodoTipo] = useState("mes");
  const [periodoMes, setPeriodoMes] = useState(mesActual());
  const [periodoAnio, setPeriodoAnio] = useState(String(new Date().getFullYear()));
  const [periodoDia, setPeriodoDia] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const [cumpleanosResumen, setCumpleanosResumen] = useState(null);
  const [appVersion, setAppVersion] = useState(null);

  // Notificaciones: avisos no leídos + tickets con respuestas nuevas
  const [avisosNoLeidos, setAvisosNoLeidos] = useState([]);
  const [ticketsNuevos, setTicketsNuevos] = useState([]);

  const puedeVerSoporte = user?.role in { admin: 1, super_admin: 1, gerente: 1 } || hasPermission("soporte.ver") || hasPermission("soporte.crear");
  const puedeVerAvisos  = user?.role in { admin: 1, super_admin: 1, gerente: 1 } || hasPermission("avisos.ver");

  const fetchNotificaciones = useCallback(async () => {
    try {
      const hdrs = { Authorization: `Bearer ${token}` };
      const fetches = [];
      if (puedeVerAvisos)  fetches.push(fetch(`${API}/admin/avisos`,   { headers: hdrs }).then(r => r.ok ? r.json() : null).then(d => d && setAvisosNoLeidos(d.filter(a => !a.leido))));
      if (puedeVerSoporte) fetches.push(fetch(`${API}/admin/soporte`, { headers: hdrs }).then(r => r.ok ? r.json() : null).then(d => d && setTicketsNuevos(d.filter(t => t.tiene_novedades && (t.estado !== "cerrado" || t.marcado_no_leido)).sort((a, b) => (b.mencion_nueva ? 1 : 0) - (a.mencion_nueva ? 1 : 0)))));
      await Promise.all(fetches);
    } catch { }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, puedeVerAvisos, puedeVerSoporte]);

  // SSE — actualizar badges de notificaciones en tiempo real cuando hay ticket nuevo o actualizado
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "ticket_nuevo" || event.tipo === "ticket_actualizado") {
        fetchNotificaciones();
      }
    }, [fetchNotificaciones]),
  });

  const dismissAviso = async (id) => {
    try {
      await fetch(`${API}/admin/avisos/${id}/leer`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    } catch { }
    setAvisosNoLeidos(prev => prev.filter(a => a.id !== id));
  };

  // Descartar = marcar como leído en el servidor (antes solo se ocultaba hasta recargar)
  const dismissTicket = (id) => {
    setTicketsNuevos(prev => prev.filter(t => t.id !== id));
    fetch(`${API}/admin/soporte/${id}/vista`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };
  const dismissTodosTickets = () => {
    setTicketsNuevos([]);
    fetch(`${API}/admin/soporte/marcar-todas-vistas`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  // El chequeo de licencia ahora es global (ver <LicenciaGate/> en App.js) — se
  // hace una sola vez en AuthContext y no depende de estar en esta página.

  useEffect(() => {
    fetchData();
    // Pre-calentar localStorage de Ventas/Egresos para todas las empresas
    const t = setTimeout(() => { prefetchVentasData(empresasPropias, token).catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [activeEmpresaPropia, periodoTipo, periodoMes, periodoAnio, periodoDia]); // eslint-disable-line

  useEffect(() => {
    fetchNotificaciones();
    const interval = setInterval(fetchNotificaciones, 60000);
    return () => clearInterval(interval);
  }, [token]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
      const resumenParams = new URLSearchParams({ periodo_tipo: periodoTipo === "dia" ? "mes" : periodoTipo });
      if (periodoTipo === "mes") resumenParams.set("mes", periodoMes);
      if (periodoTipo === "anio") resumenParams.set("anio", periodoAnio);
      if (periodoTipo === "dia") resumenParams.set("mes", periodoDia);
      if (activeEmpresaPropia?.slug) resumenParams.set("logo_tipo", activeEmpresaPropia.slug);
      const fetches = [
        fetch(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/stats${q}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/dashboard/resumen?${resumenParams}`, { headers: { Authorization: `Bearer ${token}` } }),
      ];
      if (hasPermission("alertas.ver")) {
        fetches.push(fetch(`${API}/admin/alertas/cumpleanos/resumen${q}`, { headers: { Authorization: `Bearer ${token}` } }));
      }
      const results = await Promise.all(fetches);
      const [messagesRes, statsRes, resumenRes, cumpleanosRes] = results;

      if (messagesRes.ok) {
        const messagesData = await messagesRes.json();
        setMessages(messagesData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      if (resumenRes.ok) {
        setResumen(await resumenRes.json());
      }
      // Cuotas stats (best-effort)
      try {
        const cuotasQ = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
        const r = await fetch(`${API}/admin/cuotas/stats${cuotasQ}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setCuotasStats(await r.json());
      } catch {};
      if (cumpleanosRes?.ok) {
        setCumpleanosResumen(await cumpleanosRes.json());
      } else {
        setCumpleanosResumen(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── Pre-carga de datos del mes actual para VentasPage y EgresosPage ──────────
  // Se ejecuta en background después de que el dashboard carga sus propios datos.
  // Rellena el localStorage con el formato que usan VentasPage y EgresosPage,
  // de modo que al navegar a esas páginas los datos aparecen sin spinner.
  const prefetchVentasData = async (propias, tkn) => {
    if (!propias?.length || !tkn) return;
    const mes = mesActual(); // YYYY-MM del mes actual
    const hdrs = { Authorization: `Bearer ${tkn}` };
    const PREFETCH_TTL = 30 * 60 * 1000; // re-fetch si tiene más de 30 min

    const lsFresh = (key) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        const { ts } = JSON.parse(raw);
        return Date.now() - ts < PREFETCH_TTL;
      } catch { return false; }
    };
    const lsSave = (key, data) => {
      try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
    };

    // Procesa cada empresa en paralelo, sin bloquear nada
    await Promise.allSettled(propias.map(async (emp) => {
      const slug = emp.slug;
      if (!slug) return;

      const fetchTasks = [];

      // Facturas → ard_facturas_${slug}|${mes}|false
      const facKey = `ard_facturas_${slug}|${mes}|false`;
      if (!lsFresh(facKey)) {
        fetchTasks.push(
          fetch(`${API}/admin/facturas?mes=${mes}&logo_tipo=${slug}`, { headers: hdrs })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              const processed = data.filter(f => !f.eliminada).map(f => ({
                ...f,
                fecha: f.fecha ? f.fecha.slice(0, 10) : null,
                fecha_vencimiento: f.fecha_vencimiento ? f.fecha_vencimiento.slice(0, 10) : null,
                fecha_pago: f.fecha_pago ? f.fecha_pago.slice(0, 10) : null,
              }));
              lsSave(facKey, processed);
            })
            .catch(() => {})
        );
      }

      // Presupuestos → ard_presupuestos_${slug}|${mes}||false  (anioParam="" cuando filtroTipo=mes)
      const presKey = `ard_presupuestos_${slug}|${mes}||false`;
      if (!lsFresh(presKey)) {
        fetchTasks.push(
          fetch(`${API}/admin/presupuestos?mes=${mes}&logo_tipo=${slug}`, { headers: hdrs })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              lsSave(presKey, data.filter(x => !x.eliminada));
            })
            .catch(() => {})
        );
      }

      // Compras → ard_compras_${slug}|${mes}||false
      const compKey = `ard_compras_${slug}|${mes}||false`;
      if (!lsFresh(compKey)) {
        fetchTasks.push(
          fetch(`${API}/admin/compras?mes=${mes}&logo_tipo=${slug}`, { headers: hdrs })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              lsSave(compKey, data.filter(c => !c.eliminada));
            })
            .catch(() => {})
        );
      }

      await Promise.allSettled(fetchTasks);
    }));
  };

  const handleLogout = () => {
    logout();
    toast.success("Sesión cerrada");
    navigate("/");
  };

  const markAsRead = async (messageId) => {
    try {
      const response = await fetch(`${API}/admin/messages/${messageId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setMessages(messages.map(m => 
          m.id === messageId ? { ...m, read: true } : m
        ));
        setStats({ ...stats, unread_messages: stats.unread_messages - 1, read_messages: stats.read_messages + 1 });
      }
    } catch (error) {
      toast.error("Error al marcar como leído");
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("¿Está seguro de eliminar este mensaje?")) return;

    try {
      const response = await fetch(`${API}/admin/messages/${messageId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const deletedMsg = messages.find(m => m.id === messageId);
        setMessages(messages.filter(m => m.id !== messageId));
        setStats({
          ...stats,
          total_messages: stats.total_messages - 1,
          unread_messages: deletedMsg?.read ? stats.unread_messages : stats.unread_messages - 1,
          read_messages: deletedMsg?.read ? stats.read_messages - 1 : stats.read_messages
        });
        setSelectedMessage(null);
        toast.success("Mensaje eliminado");
      }
    } catch (error) {
      toast.error("Error al eliminar mensaje");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-PY", { 
      day: "numeric", 
      month: "short", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const hayModalAbierto = !!(selectedMessage || showBackup);
  useAutoRefresh(fetchData, 30_000, !hayModalAbierto);

  // Versión del sistema — se carga una sola vez al montar
  React.useEffect(() => {
    fetch(`${API}/version`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.version && setAppVersion(d.version))
      .catch(() => {});
  }, []);

  // El bloqueo por licencia vencida/manual ahora se muestra globalmente desde
  // <LicenciaGate/> (App.js), cubre cualquier página bajo /sistema.

  return (
    <div className="min-h-screen bg-arandu-dark flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-arandu-dark-light border-r border-white/5 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <LogoDisplay activeEmpresaPropia={activeEmpresaPropia} />
            </div>
          </div>
          {/* Empresa switcher */}
          <div className="pt-3 border-b border-white/5">
            <EmpresaSwitcher />
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <div className="px-4 py-3 bg-arandu-blue/10 border border-arandu-blue/20 rounded-lg flex items-center gap-3 text-arandu-blue">
              <BarChart3 className="w-5 h-5" />
              <span className="font-body font-medium">Dashboard</span>
            </div>
            {hasModule("mensajes") && (
              <button className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all">
                <Inbox className="w-5 h-5" />
                <span className="font-body">Mensajes</span>
                {stats.unread_messages > 0 && (
                  <span className="ml-auto bg-arandu-red text-white text-xs px-2 py-1 rounded-full">
                    {stats.unread_messages}
                  </span>
                )}
              </button>
            )}
            {/* Catastro unificado */}
            {(hasPermission("empresas.ver") || hasPermission("proveedores.ver") || hasPermission("empleados.ver") || hasPermission("localidades.ver") || hasPermission("configuracion_catastro.gestionar_sueldos") || hasPermission("configuracion_catastro.gestionar_huella")) && (
              <Link
                to="/sistema/catastro"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <Users className="w-5 h-5" />
                <span className="font-body">Catastro</span>
              </Link>
            )}
            {(hasPermission("presupuestos.ver") || hasPermission("facturas.ver") || hasPermission("ingresos_varios.ver") || hasPermission("recibos.ver") || hasPermission("notas_credito.ver")) && (
              <Link
                to="/sistema/ventas"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-body">Ventas</span>
                {stats.total_presupuestos > 0 && (
                  <span className="ml-auto text-slate-600 text-xs">{stats.total_presupuestos}</span>
                )}
              </Link>
            )}
            {hasPermission("pdv.ver") && (
              <Link
                to="/sistema/pdv"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <Zap className="w-5 h-5" />
                <span className="font-body">PDV</span>
              </Link>
            )}
            {(hasPermission("compras.ver") || hasPermission("costos_fijos.ver") || hasPermission("empleados.ver") || hasPermission("pagos_proveedores.ver") || hasPermission("balance.ver")) && (
              <Link
                to="/sistema/egresos"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <ShoppingBag className="w-5 h-5" />
                <span className="font-body">Egresos</span>
              </Link>
            )}
            {hasPermission("balance.ver") && (
              <Link
                to="/sistema/balance"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <Scale className="w-5 h-5" />
                <span className="font-body">Balance</span>
              </Link>
            )}
            {hasPermission("plan_cuentas.ver") && (
              <Link
                to="/sistema/plan-cuentas"
                data-testid="menu-plan-cuentas"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <BookOpen className="w-5 h-5 text-cyan-400" />
                <span className="font-body">Plan de cuentas</span>
              </Link>
            )}
            {(["admin", "gerente", "super_admin"].includes(user?.role) || hasPermission("transferencias.ver") || hasPermission("bancos.ver")) && (
              <Link
                to="/sistema/financiero"
                data-testid="menu-financiero"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              >
                <Repeat2 className="w-5 h-5 text-emerald-400" />
                <span className="font-body">Financiero</span>
              </Link>
            )}
            {hasPermission("inventario.ver") && (
              <Link
                to="/sistema/inventario"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-inventario"
              >
                <Server className="w-5 h-5" />
                <span className="font-body">Inventario Técnico</span>
              </Link>
            )}
            {hasPermission("inventario_productos.ver") && (
              <Link
                to="/sistema/productos"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-productos"
              >
                <Package className="w-5 h-5 text-cyan-500" />
                <span className="font-body">Catálogo Productos</span>
              </Link>
            )}

            {(hasPermission("lectura_bombas.ver") || hasPermission("lectura_bombas.configurar")) && (
              <Link
                to="/sistema/lectura-bombas"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-lectura-bombas"
              >
                <Fuel className="w-5 h-5 text-orange-400" />
                <span className="font-body">Lectura de Bombas</span>
              </Link>
            )}
            {hasPermission("produccion.ver") && (
              <Link
                to="/sistema/produccion"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-produccion"
              >
                <ChefHat className="w-5 h-5 text-violet-400" />
                <span className="font-body">Producción</span>
              </Link>
            )}
            {hasPermission("hoteleria.ver_panel") && (
              <Link
                to="/sistema/hoteleria"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-hoteleria"
              >
                <BedDouble className="w-5 h-5 text-cyan-400" />
                <span className="font-body">Hotelería</span>
              </Link>
            )}
            {hasPermission("loteadora.ver") && (
              <Link
                to="/sistema/loteadora"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-loteadora"
              >
                <MapPin className="w-5 h-5 text-emerald-400" />
                <span className="font-body">Loteadora</span>
              </Link>
            )}
            {hasPermission("transporte.ver") && (
              <Link
                to="/sistema/transporte"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-transporte"
              >
                <Truck className="w-5 h-5 text-amber-400" />
                <span className="font-body">Transporte</span>
              </Link>
            )}
            {hasPermission("taller.ver") && (
              <Link
                to="/sistema/taller"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-taller"
              >
                <Wrench className="w-5 h-5 text-orange-400" />
                <span className="font-body">Taller</span>
              </Link>
            )}
            {["ver", "cocina", "mozo", "disponibilidad", "configurar"].some(a => hasPermission(`restaurante.${a}`)) && (
              <Link
                to="/sistema/restaurante"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-restaurante"
              >
                <ChefHat className="w-5 h-5 text-orange-400" />
                <span className="font-body">Restaurante</span>
              </Link>
            )}
            {hasPermission("importaciones.ver") && (
              <Link
                to="/sistema/importaciones"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-importaciones"
              >
                <Ship className="w-5 h-5 text-sky-400" />
                <span className="font-body">Importaciones</span>
              </Link>
            )}
            {hasPermission("bascula.ver") && (
              <Link
                to="/sistema/bascula"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-bascula"
              >
                <Scale className="w-5 h-5 text-emerald-400" />
                <span className="font-body">Báscula</span>
              </Link>
            )}
            {hasPermission("equipos_bascula.ver") && (
              <Link
                to="/sistema/equipos-bascula"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-equipos-bascula"
              >
                <Scale className="w-5 h-5 text-teal-400" />
                <span className="font-body">Equipos / Báscula</span>
              </Link>
            )}
            {hasPermission("clinica_agenda.ver") && (
              <Link
                to="/sistema/clinica"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-clinica"
              >
                <Stethoscope className="w-5 h-5 text-teal-400" />
                <span className="font-body">Clínica</span>
              </Link>
            )}
            {hasPermission("recetario.ver") && (
              <Link
                to="/sistema/recetario"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-recetario"
              >
                <FileText className="w-5 h-5 text-lime-400" />
                <span className="font-body">Recetario</span>
              </Link>
            )}
            {hasPermission("constructora_proyectos.ver") && (
              <Link
                to="/sistema/constructora"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-constructora"
              >
                <HardHat className="w-5 h-5 text-yellow-400" />
                <span className="font-body">Constructora</span>
              </Link>
            )}
            {hasPermission("reportes.ver") && (
              <Link
                to="/sistema/reportes"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-reportes"
              >
                <FileText className="w-5 h-5" />
                <span className="font-body">Reportes</span>
              </Link>
            )}
            {hasPermission("configuracion_global.ver") && (
              <Link
                to="/sistema/config-global"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-config-global"
              >
                <SlidersHorizontal className="w-5 h-5 text-blue-400" />
                <span className="font-body">Configuración global</span>
              </Link>
            )}
            {hasPermission("usuarios.ver") && (
              <Link 
                to="/sistema/usuarios"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-usuarios"
              >
                <Users className="w-5 h-5" />
                <span className="font-body">Usuarios</span>
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "gerente" || hasPermission("auditoria.ver")) && (
              <Link
                to="/sistema/auditoria"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-auditoria"
              >
                <Shield className="w-5 h-5" />
                <span className="font-body">Auditoria</span>
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "gerente") && (
              <Link
                to="/sistema/empresas-propias"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-empresas-propias"
              >
                <Building2 className="w-5 h-5 text-violet-400" />
                <span className="font-body">Mis Empresas</span>
              </Link>
            )}
            <Link
              to="/sistema/soporte"
              className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
              data-testid="nav-soporte"
            >
              <LifeBuoy className="w-5 h-5 text-arandu-blue" />
              <span className="font-body">Soporte</span>
            </Link>
            {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente") && (
              <Link
                to="/sistema/licencias"
                className="w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition-all"
                data-testid="nav-licencias"
              >
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <span className="font-body">Licencias</span>
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-3 px-2">
              <Link to="/sistema/perfil" className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-arandu-blue/20 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-arandu-blue/40 transition-all cursor-pointer">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-arandu-blue" />
                  )}
                </div>
              </Link>
              <div className="flex-1 min-w-0">
                <p className="text-white font-body text-sm truncate">{user?.name}</p>
                <p className="text-slate-500 text-xs truncate">{user?.email}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded mt-0.5 inline-block ${
                  user?.role === "admin" ? "bg-arandu-red/20 text-arandu-red" :
                  user?.role === "gerente" ? "bg-violet-500/20 text-violet-400" :
                  "bg-arandu-blue/20 text-arandu-blue"
                }`}>
                  {user?.role === "admin" ? "Admin" : user?.role === "gerente" ? "Administrador" : "Usuario"}
                </span>
              </div>
            </div>
            <Link
              to="/sistema/perfil"
              className="w-full px-4 py-2 text-slate-400 hover:text-arandu-blue hover:bg-arandu-blue/10 rounded-lg flex items-center gap-2 transition-all mb-1"
              data-testid="nav-perfil"
            >
              <User className="w-4 h-4" />
              <span className="font-body text-sm">Mi Perfil</span>
            </Link>
            {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente" ||
              (user?.permisos || []).some(p => p.startsWith("backup."))) && (
              <button
                onClick={() => setShowBackup(true)}
                className="w-full px-4 py-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg flex items-center gap-2 transition-all mb-1"
                data-testid="backup-btn"
              >
                <HardDriveDownload className="w-4 h-4" />
                <span className="font-body text-sm">BackUp de mis datos</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-slate-400 hover:text-arandu-red hover:bg-arandu-red/10 rounded-lg flex items-center gap-2 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="font-body text-sm">Cerrar Sesión</span>
            </button>
            {appVersion && (
              <div className="mt-2 flex items-center justify-center">
                <span className="text-slate-600 text-xs font-mono tracking-wide">
                  v{appVersion}
                </span>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* BackUp Modal */}
      <BackupModal open={showBackup} onClose={() => setShowBackup(false)} />

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-arandu-dark/80 backdrop-blur-lg border-b border-white/5">
          <div className="px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-white"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-white">
                  Panel de Administración
                </h1>
                <p className="text-slate-500 text-sm font-body">
                  Bienvenido, {user?.name?.split(" ")[0]}
                </p>
              </div>
            </div>
            {/* Botón "Ver Sitio" eliminado */}
          </div>
        </header>

        <div className="p-4 md:p-8">
          {/* Empresa activa banner */}
          {activeEmpresaPropia && (
            <div
              className="flex items-center gap-2 mb-5 px-4 py-2 rounded-xl border text-sm font-body"
              style={{
                backgroundColor: `${activeEmpresaPropia.color || "#3b82f6"}15`,
                borderColor: `${activeEmpresaPropia.color || "#3b82f6"}30`,
              }}
            >
              {resolveLogoForContext(activeEmpresaPropia, "panel") ? (
                <span className="w-10 h-7 flex items-center justify-center flex-shrink-0 rounded bg-white/85">
                  <img src={resolveLogoForContext(activeEmpresaPropia, "panel")} alt={activeEmpresaPropia.nombre} className="max-w-full max-h-full object-contain" />
                </span>
              ) : (
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: activeEmpresaPropia.color || "#3b82f6" }} />
              )}
              <span className="text-white font-medium">{activeEmpresaPropia.nombre}</span>
            </div>
          )}

          {/* El aviso de licencia por vencer ahora es un modal global obligatorio — ver <LicenciaGate/> en App.js */}

          {/* ── Banners de notificaciones ── */}
          <AnimatePresence>
            {puedeVerAvisos && avisosNoLeidos.map(aviso => (
              <motion.div key={aviso.id}
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mb-3"
              >
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-violet-500/30 bg-violet-500/10">
                  <Megaphone className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{aviso.titulo}</p>
                    <p className="text-violet-300/80 text-xs mt-0.5 line-clamp-2">{aviso.contenido}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => { dismissAviso(aviso.id); navigate("/sistema/soporte?tab=avisos"); }}
                      className="text-violet-400 hover:text-white text-xs px-2 py-1 rounded border border-violet-500/30 hover:border-violet-400/60 transition-all">
                      Ver
                    </button>
                    <button onClick={() => dismissAviso(aviso.id)} title="Marcar como leído"
                      className="p-1.5 text-violet-400/60 hover:text-violet-300 transition-all">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
            {puedeVerSoporte && ticketsNuevos.length > 1 && (
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs text-slate-400">{ticketsNuevos.length} tickets con novedades</span>
                <button onClick={dismissTodosTickets} className="text-xs text-arandu-blue hover:text-white px-2 py-1 rounded border border-arandu-blue/30 hover:border-arandu-blue/60 transition-all">
                  Marcar todos como leídos
                </button>
              </div>
            )}
            {puedeVerSoporte && ticketsNuevos.map(ticket => (
              <motion.div key={ticket.id}
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mb-3"
              >
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-arandu-blue/30 bg-arandu-blue/10">
                  <BellRing className="w-4 h-4 text-arandu-blue mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{ticket.mencion_nueva ? "Te mencionaron en el ticket" : "Ticket respondido"}: {ticket.titulo}</p>
                    <p className="text-arandu-blue/80 text-xs mt-0.5">
                      {ticket.numero} · {ticket.empresa_nombre || ""} — Tenés respuestas nuevas
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Link to={`/sistema/soporte?ticket=${ticket.id}`}
                      className="text-arandu-blue hover:text-white text-xs px-2 py-1 rounded border border-arandu-blue/30 hover:border-arandu-blue/60 transition-all">
                      Ver
                    </Link>
                    <button onClick={() => dismissTicket(ticket.id)} title="Descartar"
                      className="p-1.5 text-arandu-blue/60 hover:text-arandu-blue transition-all">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Periodo estadísticas */}
          <div className="flex items-center gap-2 flex-wrap mb-5 bg-arandu-dark-light border border-white/10 rounded-xl px-4 py-3">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 text-sm mr-1">Periodo:</span>
            {[
              ["mes", "Por mes"],
              ["dia", "Por día"],
              ["anio", "Por año"],
              ["todos", "Todo"],
            ].map(([v, label]) => (
              <button key={v} onClick={() => setPeriodoTipo(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${periodoTipo === v ? "bg-arandu-blue border-arandu-blue text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}>
                {label}
              </button>
            ))}
            {periodoTipo === "mes" && (
              <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
                <button onClick={() => setPeriodoMes(prevMes(periodoMes))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-white text-sm font-medium min-w-[110px] text-center px-2 capitalize">{mesLabel(periodoMes)}</span>
                <button onClick={() => setPeriodoMes(nextMes(periodoMes))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {periodoTipo === "dia" && (
              <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
                <button onClick={() => setPeriodoDia(prevDia(periodoDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-white text-sm font-medium min-w-[120px] text-center px-2 capitalize">{diaLabel(periodoDia)}</span>
                <button onClick={() => setPeriodoDia(nextDia(periodoDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {periodoTipo === "anio" && (
              <input type="number" value={periodoAnio} onChange={e => setPeriodoAnio(e.target.value)}
                className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm w-24" />
            )}
            <span className="ml-auto text-slate-500 text-xs">
              {periodoTipo === "todos" ? "Resumen completo" : periodoTipo === "mes" ? mesLabel(periodoMes) : periodoTipo === "dia" ? diaLabel(periodoDia) : periodoAnio}
            </span>
          </div>

          {/* Badge de scope activo */}
          {(() => {
            const scope = user?.dashboard_config?.scope;
            if (!scope || scope === "todo") return null;
            const scopeLabels = { usuario: "Vista: solo mis transacciones", personalizado: "Vista: usuarios seleccionados" };
            const label = scopeLabels[scope];
            if (!label) return null;
            return (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 text-xs w-fit">
                <Eye className="w-3.5 h-3.5" />
                <span>{label}</span>
              </div>
            );
          })()}

          {/* Estadísticas operativas */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            {[
              { perm: hasPermission("alertas.ver") && cumpleanosResumen, title: "Cumpleaños", icon: Bell, color: "text-pink-400", value: cumpleanosResumen?.hoy ?? 0, lines: [`Hoy (rojo): ${cumpleanosResumen?.hoy ?? 0}`, `Próximos (amarillo): ${cumpleanosResumen?.proximos ?? 0}`, `En ventana: ${cumpleanosResumen?.total ?? 0}`], link: "/sistema/reportes?tab=alertas" },
              { perm: hasModule("mensajes"), title: "Mensajes", icon: MessageSquare, color: "text-blue-400", value: resumen?.mensajes?.total || 0, lines: [`Sin leer: ${resumen?.mensajes?.sin_leer || 0}`] },
              { perm: (hasPermission("empresas.ver") || hasPermission("proveedores.ver") || hasPermission("empleados.ver")), title: "Entidades", icon: Building2, color: "text-purple-400", value: resumen?.clientes?.total || 0, lines: ["entidades registradas"] },
              { perm: hasPermission("presupuestos.ver"), title: "Presupuestos", icon: FileText, color: "text-green-400", value: resumen?.presupuestos?.total || 0, lines: [`Aprobados: ${resumen?.presupuestos?.aprobados || 0}`, `Rechazados: ${resumen?.presupuestos?.rechazados || 0}`, `Cobrados: ${resumen?.presupuestos?.cobrados || 0}`, `Faltantes: ${resumen?.presupuestos?.faltantes || 0}`] },
              { perm: hasPermission("facturas.ver"), title: "Facturación", icon: Receipt, color: "text-cyan-400", value: fmtMixed(resumen?.facturacion?.total, resumen?.facturacion?.total_usd, resumen?.facturacion?.total_brl), lines: [
                  `Facturas: ${resumen?.facturacion?.cantidad || 0}`,
                  `Cobrado: ${fmtMixed(resumen?.facturacion?.cobrado, resumen?.facturacion?.cobrado_usd, resumen?.facturacion?.cobrado_brl)}`,
                  `Pendiente: ${fmtMixed(resumen?.facturacion?.pendiente, resumen?.facturacion?.pendiente_usd, resumen?.facturacion?.pendiente_brl)}`,
                  ...(cuotasStats?.ventas?.count_pendientes != null
                    ? [`Cuotas pend.: ${cuotasStats.ventas.count_pendientes}${cuotasStats.ventas.count_vencidas > 0 ? ` · ⚠ ${cuotasStats.ventas.count_vencidas} vencida(s)` : ""}`]
                    : [])
              ] },
              { perm: hasPermission("ingresos_varios.ver"), title: "Ingresos", icon: TrendingUp, color: "text-emerald-400", value: fmtMixed(resumen?.ingresos?.total, resumen?.ingresos?.total_usd, resumen?.ingresos?.total_brl), lines: [`Registros: ${resumen?.ingresos?.cantidad || 0}`] },
              { perm: hasPermission("compras.ver"), title: "Compras", icon: ShoppingBag, color: "text-orange-400", value: fmtMixed(resumen?.compras?.total, resumen?.compras?.total_usd, resumen?.compras?.total_brl), lines: [
                  `Contado: ${resumen?.compras?.contado || 0}`,
                  `Crédito: ${resumen?.compras?.credito || 0}`,
                  `Pendiente: ${fmtMixed(resumen?.compras?.pendiente, resumen?.compras?.pendiente_usd, resumen?.compras?.pendiente_brl)}`,
                  ...(cuotasStats?.compras?.count_pendientes != null
                    ? [`Cuotas pend.: ${cuotasStats.compras.count_pendientes}${cuotasStats.compras.count_vencidas > 0 ? ` · ⚠ ${cuotasStats.compras.count_vencidas} vencida(s)` : ""}`]
                    : [])
              ] },
              { perm: hasPermission("proveedores.ver"), title: "Proveedores", icon: Truck, color: "text-amber-400", value: fmtMixed(resumen?.proveedores?.pagado, resumen?.proveedores?.pagado_usd, resumen?.proveedores?.pagado_brl), lines: [`Pagos: ${resumen?.proveedores?.pagos || 0}`, `Debe: ${fmtMixed(resumen?.proveedores?.pendiente, resumen?.proveedores?.pendiente_usd, resumen?.proveedores?.pendiente_brl)}`] },
              { perm: hasPermission("empleados.ver"), title: "Sueldos", icon: Users, color: "text-pink-400", value: fmtMixed(resumen?.sueldos?.total, resumen?.sueldos?.total_usd), lines: [`Extras: ${fmtMixed(resumen?.sueldos?.extras, resumen?.sueldos?.extras_usd)}`, `Adelantos: ${fmtMixed(resumen?.sueldos?.adelantos, resumen?.sueldos?.adelantos_usd)}`, `Descuentos: ${fmtPYG(resumen?.sueldos?.descuentos)}`] },
              { perm: hasPermission("notas_credito.ver"), title: "Notas crédito", icon: AlertCircle, color: "text-rose-400", value: fmtMixed(resumen?.notas_credito?.total, resumen?.notas_credito?.total_usd, resumen?.notas_credito?.total_brl), lines: [`Ventas: ${resumen?.notas_credito?.ventas || 0}`, `Compras: ${resumen?.notas_credito?.compras || 0}`] },
              { perm: hasPermission("recibos.ver"), title: "Recibos", icon: DollarSign, color: "text-lime-400", value: fmtMixed(resumen?.recibos?.total, resumen?.recibos?.total_usd, resumen?.recibos?.total_brl), lines: [`Recibos: ${resumen?.recibos?.cantidad || 0}`] },
              { perm: hasPermission("costos_fijos.ver"), title: "Gastos", icon: DollarSign, color: "text-red-400", value: fmtMixed(resumen?.gastos?.pagado, resumen?.gastos?.pagado_usd, resumen?.gastos?.pagado_brl), lines: [`Pagos registrados: ${resumen?.gastos?.cantidad || 0}`] },
              { perm: hasPermission("balance.ver"), title: "IVA", icon: Scale, color: "text-violet-400", value: fmtMixed(resumen?.iva?.saldo, resumen?.iva?.saldo_usd), lines: [`A pagar: ${fmtMixed(resumen?.iva?.a_pagar, resumen?.iva?.a_pagar_usd)}`, `Pagado: ${fmtPYG(resumen?.iva?.pagado)}`] },
            ].filter(card => card.perm).map((card, i) => {
              const Icon = card.icon;
              const inner = (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-slate-400 text-sm font-body">{card.title}</p>
                    <Icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                  <p className={`font-heading font-bold text-xl leading-snug break-words ${card.color}`}>{card.value}</p>
                  <div className="mt-2 space-y-0.5">
                    {card.lines.map(line => <p key={line} className="text-slate-500 text-xs">{line}</p>)}
                  </div>
                </>
              );
              return (
                <motion.div key={card.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="bg-arandu-dark-light border border-white/5 rounded-xl p-4 min-h-[150px]">
                  {card.link ? (
                    <Link to={card.link} className="block hover:opacity-90 transition-opacity">{inner}</Link>
                  ) : inner}
                </motion.div>
              );
            })}
          </div>

          {/* Widget Control de Stock */}
          {hasPermission("inventario_productos.ver") && resumen?.stock && (resumen.stock.sin_stock > 0 || resumen.stock.en_minimo > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mb-8 bg-arandu-dark-light border border-white/5 rounded-xl p-4"
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Package className="w-5 h-5 text-cyan-400 shrink-0" />
                <span className="text-white font-heading font-semibold text-base">Control de Stock</span>
                <div className="flex items-center gap-4 ml-auto flex-wrap">
                  {resumen.stock.sin_stock > 0 && (
                    <span className="flex items-center gap-1.5 text-sm">
                      <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                      <span className="text-red-400 font-semibold">{resumen.stock.sin_stock}</span>
                      <span className="text-slate-500 text-xs">sin stock</span>
                    </span>
                  )}
                  {resumen.stock.en_minimo > 0 && (
                    <span className="flex items-center gap-1.5 text-sm">
                      <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                      <span className="text-orange-400 font-semibold">{resumen.stock.en_minimo}</span>
                      <span className="text-slate-500 text-xs">en mínimo</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-sm">
                    <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" />
                    <span className="text-slate-400 font-semibold">{resumen.stock.total_activos}</span>
                    <span className="text-slate-500 text-xs">total activos</span>
                  </span>
                  <Link
                    to="/sistema/productos"
                    className="text-xs text-arandu-blue hover:underline ml-2 flex items-center gap-1"
                  >
                    Ver catálogo <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              {/* Dos columnas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sin stock */}
                {resumen.stock.sin_stock > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-red-400 text-xs font-semibold uppercase tracking-wider">Sin stock</span>
                    </div>
                    <div className="space-y-1">
                      {resumen.stock.sin_stock_lista.slice(0, 8).map((p, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                          <PackageX className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <span className="text-slate-300 text-xs flex-1 truncate">{p.nombre}</span>
                          <span className="text-red-400 text-xs font-semibold bg-red-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {Math.round(p.stock_actual ?? 0)} unid.
                          </span>
                        </div>
                      ))}
                      {resumen.stock.sin_stock > 8 && (
                        <p className="text-slate-600 text-xs pt-1">+ {resumen.stock.sin_stock - 8} productos más...</p>
                      )}
                    </div>
                  </div>
                )}

                {/* En mínimo */}
                {resumen.stock.en_minimo > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 rounded-full bg-orange-400" />
                      <span className="text-orange-400 text-xs font-semibold uppercase tracking-wider">En mínimo / crítico</span>
                    </div>
                    <div className="space-y-1">
                      {resumen.stock.en_minimo_lista.slice(0, 8).map((p, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                          <PackageCheck className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                          <span className="text-slate-300 text-xs flex-1 truncate">{p.nombre}</span>
                          <span className="text-orange-400 text-xs font-semibold bg-orange-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {Math.round(p.stock_actual ?? 0)} / mín. {Math.round(p.stock_minimo ?? 0)}
                          </span>
                        </div>
                      ))}
                      {resumen.stock.en_minimo > 8 && (
                        <p className="text-slate-600 text-xs pt-1">+ {resumen.stock.en_minimo - 8} productos más...</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Si solo hay un tipo, ocupar las dos columnas (ya lo hace grid-cols-1 en mobile; en md se centra solo) */}
              </div>
            </motion.div>
          )}

          {/* Accesos Directos */}
          <div className="space-y-5">
            <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <ChevronRight className="w-5 h-5 text-arandu-blue" />
              Accesos Directos
            </h2>
            {[
              { title: "Ingresos", items: [
                { label: "Nuevo presupuesto", icon: FileText, path: "/sistema/ventas?nuevo=presupuesto", perm: "presupuestos.crear", color: "emerald" },
                { label: "Nueva factura", icon: Receipt, path: "/sistema/ventas?nuevo=factura", perm: "facturas.crear", color: "cyan" },
                { label: "Nueva nota crédito", icon: AlertCircle, path: "/sistema/ventas?nuevo=nota_credito", perm: "notas_credito.crear", color: "rose" },
                { label: "Nuevo ingreso", icon: TrendingUp, path: "/sistema/ventas?nuevo=ingreso", perm: "ingresos_varios.crear", color: "green" },
                { label: "Venta sin factura", icon: TrendingUp, path: "/sistema/ventas?nuevo=boleta", perm: "facturas.crear", color: "teal" },
              ]},
              { title: "Egresos", items: [
                { label: "Nuevo gasto", icon: DollarSign, path: "/sistema/egresos?nuevo=gasto", perm: "costos_fijos.crear", color: "red" },
                { label: "Nueva compra", icon: ShoppingBag, path: "/sistema/egresos?nuevo=compra", perm: "compras.crear", color: "orange" },
                { label: "Nuevo pago proveedor", icon: Truck, path: "/sistema/egresos?nuevo=pago_proveedor", perm: "pagos_proveedores.crear", color: "amber" },
                { label: "Nuevo pago IVA", icon: Scale, path: "/sistema/egresos?nuevo=pago_iva", perm: "balance.editar", color: "violet" },
              ]},
              { title: "Administrativo", items: [
                { label: "Nueva entidad", icon: Users, path: "/sistema/catastro?nuevo=1", perm: "entidades.crear", color: "violet" },
                { label: "Nuevo empleado", icon: UserCheck, path: "/sistema/catastro?nuevo=empleado", perm: "entidades.crear", color: "pink" },
                { label: "Nuevo dispositivo", icon: Server, path: "/sistema/inventario?nuevo=dispositivo", perm: "inventario.crear", color: "blue" },
                { label: "Nuevo producto", icon: Package, path: "/sistema/productos?nuevo=producto", perm: "inventario_productos.crear", color: "teal" },
                { label: "Nueva alerta", icon: Bell, path: "/sistema/reportes?nuevo=alerta", perm: "alertas.crear", color: "red" },
                { label: "Nuevo ticket", icon: LifeBuoy, path: "/sistema/soporte?nuevo=ticket", perm: "soporte.crear", color: "cyan" },
              ]},
            ].map(section => {
              const items = section.items.filter(item => hasPermission(item.perm));
              if (items.length === 0) return null;
              return (
                <div key={section.title} className="bg-arandu-dark-light border border-white/5 rounded-xl p-4">
                  <p className="text-slate-300 text-sm font-semibold mb-3">{section.title}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {items.map(item => {
                      const Icon = item.icon;
                      const colorMap = {
                        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/45",
                        cyan: "border-cyan-500/25 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-500/45",
                        rose: "border-rose-500/25 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 hover:border-rose-500/45",
                        green: "border-green-500/25 bg-green-500/10 text-green-300 hover:bg-green-500/20 hover:border-green-500/45",
                        red: "border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:border-red-500/45",
                        orange: "border-orange-500/25 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 hover:border-orange-500/45",
                        amber: "border-amber-500/25 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:border-amber-500/45",
                        violet: "border-violet-500/25 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 hover:border-violet-500/45",
                        purple: "border-purple-500/25 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 hover:border-purple-500/45",
                        yellow: "border-yellow-500/25 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20 hover:border-yellow-500/45",
                        pink: "border-pink-500/25 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 hover:border-pink-500/45",
                        blue: "border-blue-500/25 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:border-blue-500/45",
                        teal: "border-teal-500/25 bg-teal-500/10 text-teal-300 hover:bg-teal-500/20 hover:border-teal-500/45"
                      };
                      const colorClass = colorMap[item.color] || "border-white/10 bg-white/5 text-slate-300 hover:bg-arandu-blue/10 hover:border-arandu-blue/40";
                      return (
                        <button key={item.path} onClick={() => navigate(item.path)}
                          className={`border rounded-xl p-3 flex flex-col items-center gap-2 hover:text-white transition-all min-h-[92px] ${colorClass}`}>
                          <Icon className="w-6 h-6" />
                          <span className="font-body text-xs font-medium text-center leading-tight">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
